
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Mail, Phone, MapPin, GraduationCap, Star, CheckCircle } from 'lucide-react';

interface Profile {
  fullName: string;
  email: string;
  phone: string;
  location: string;
  educationLevel: string;
  skills: string[];
  registeredAt: string;
}

interface Student {
  fullName: string;
  email: string;
  phone: string;
  location: string;
  educationLevel: string;
  registeredAt: string;
}

interface AdminViewProps {
  students: Student[];
  getStudentSkills: (email: string) => string[];
  savedProfiles: Profile[];
  enrolledCourses: { [email: string]: string[] };
}

const SKILLS_LIST: { [key: string]: { name: string; icon: string; color: string } } = {
  word: { name: 'Microsoft Word', icon: 'ri-file-word-line', color: 'from-blue-500 to-blue-600' },
  excel: { name: 'Microsoft Excel', icon: 'ri-file-excel-line', color: 'from-green-500 to-green-600' },
  powerpoint: { name: 'Microsoft PowerPoint', icon: 'ri-file-ppt-line', color: 'from-orange-500 to-orange-600' },
  basic: { name: 'Basic Computer Skills', icon: 'ri-computer-line', color: 'from-purple-500 to-purple-600' },
  internet: { name: 'Internet & Email', icon: 'ri-globe-line', color: 'from-teal-500 to-teal-600' },
  cv: { name: 'CV Writing', icon: 'ri-file-user-line', color: 'from-indigo-500 to-indigo-600' },
  freelancing: { name: 'Freelancing Basics', icon: 'ri-briefcase-line', color: 'from-pink-500 to-pink-600' },
  ai: { name: 'AI Tools & Automation', icon: 'ri-robot-line', color: 'from-red-500 to-red-600' },
};

const AdminView: React.FC<AdminViewProps> = ({ students, getStudentSkills, savedProfiles, enrolledCourses }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSkill, setFilterSkill] = useState<string>('all');

  const filteredStudents = students.filter(student => {
    const matchesSearch = student.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase());
    if (filterSkill === 'all') return matchesSearch;
    return matchesSearch && getStudentSkills(student.email).includes(filterSkill);
  });

  const skillStats = Object.keys(SKILLS_LIST).map(skillId => ({
    id: skillId,
    name: SKILLS_LIST[skillId].name,
    count: students.filter(s => getStudentSkills(s.email).includes(skillId)).length,
    icon: SKILLS_LIST[skillId].icon,
    color: SKILLS_LIST[skillId].color,
  })).sort((a, b) => b.count - a.count);

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Stats Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-4"
      >
        <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <Users className="w-8 h-8 text-blue-600" />
            <span className="text-3xl font-bold text-gray-900">{students.length}</span>
          </div>
          <p className="text-gray-600 font-medium">Total Students</p>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <Star className="w-8 h-8 text-green-600" />
            <span className="text-3xl font-bold text-gray-900">{savedProfiles.length}</span>
          </div>
          <p className="text-gray-600 font-medium">Profiles Created</p>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <CheckCircle className="w-8 h-8 text-purple-600" />
            <span className="text-3xl font-bold text-gray-900">
              {students.filter(s => getStudentSkills(s.email).length > 0).length}
            </span>
          </div>
          <p className="text-gray-600 font-medium">Skills Selected</p>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <GraduationCap className="w-8 h-8 text-orange-600" />
            <span className="text-3xl font-bold text-gray-900">
              {Object.keys(SKILLS_LIST).length}
            </span>
          </div>
          <p className="text-gray-600 font-medium">Available Skills</p>
        </div>
      </motion.div>

      {/* Skills Popularity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-3xl shadow-2xl p-6"
      >
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-500" />
          Skills Popularity
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {skillStats.map(skill => (
            <div
              key={skill.id}
              className="p-3 rounded-xl border border-gray-200 hover:shadow-md transition-all"
            >
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${skill.color} flex items-center justify-center mb-2`}>
                <i className={`${skill.icon} text-white`}></i>
              </div>
              <p className="font-bold text-gray-900 text-sm">{skill.name}</p>
              <p className="text-xs text-gray-500">{skill.count} student{skill.count !== 1 ? 's' : ''}</p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Students List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Registered Students ({students.length})
            </h3>
            <div className="flex gap-3">
              <input
                type="text"
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
              />
              <select
                value={filterSkill}
                onChange={(e) => setFilterSkill(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
              >
                <option value="all">All Skills</option>
                {Object.entries(SKILLS_LIST).map(([id, skill]) => (
                  <option key={id} value={id}>{skill.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {filteredStudents.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No students found</p>
            <p className="text-gray-400 text-sm">
              {students.length === 0 ? 'Register students to see them here' : 'Try adjusting your search filters'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Student</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Contact</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Location</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Education</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Selected Skills</th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Registered</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredStudents.map((student, index) => {
                  const skills = getStudentSkills(student.email);
                  return (
                    <motion.tr
                      key={student.email}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-red-600 flex items-center justify-center text-white font-bold text-sm">
                            {student.fullName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{student.fullName}</p>
                            <p className="text-xs text-gray-500 capitalize">{student.educationLevel} Level</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Mail className="w-3.5 h-3.5" />
                            <span className="truncate max-w-[200px]">{student.email}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="w-3.5 h-3.5" />
                            <span>{student.phone}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="w-3.5 h-3.5" />
                          <span>{student.location}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium capitalize">
                          {student.educationLevel}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {skills.length > 0 ? (
                            skills.slice(0, 3).map(skillId => {
                              const skill = SKILLS_LIST[skillId];
                              return skill ? (
                                <span
                                  key={skillId}
                                  className="px-2 py-1 bg-gradient-to-r from-green-50 to-teal-50 text-green-700 rounded-lg text-xs font-medium"
                                >
                                  {skill.name}
                                </span>
                              ) : null;
                            })
                          ) : (
                            <span className="text-xs text-gray-400">No skills selected</span>
                          )}
                          {skills.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium">
                              +{skills.length - 3} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs text-gray-500">
                          {new Date(student.registeredAt).toLocaleDateString()}
                        </span>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Saved Profiles */}
      {savedProfiles.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-3xl shadow-2xl p-6"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Saved Skill Profiles
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {savedProfiles.map((profile, index) => (
              <div
                key={index}
                className="p-4 border border-gray-200 rounded-xl hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center text-white font-bold text-sm">
                    {profile.fullName.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{profile.fullName}</p>
                    <p className="text-xs text-gray-500">{profile.email}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {profile.skills.map((skillId: string) => {
                    const skill = SKILLS_LIST[skillId];
                    return skill ? (
                      <span
                        key={skillId}
                        className="px-2 py-1 bg-green-50 text-green-700 rounded-lg text-xs font-medium"
                      >
                        {skill.name}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AdminView;
